import { create } from 'zustand';
import type { FieldEvent, FieldEventsResponse, NewFieldEventInput } from '../types/fieldEvent';
import { fieldEventService } from '../services/fieldEventService';
import { withLoading } from './storeHelper';

/**
 * 圃場イベントストア
 *
 * 圃場ごとのイベント履歴とステートを管理
 * fieldEventService を呼び出して API からデータを取得
 *
 * ステート計算はバックエンド側で行う（asOf パラメータで基準日を指定）
 */
interface FieldEventStore {
  /** 圃場ごとのイベント＆ステート（APIレスポンスをそのまま保持） */
  fieldEventsMap: Record<string, FieldEventsResponse>;

  /** ローディング状態 */
  loading: boolean;

  /** エラーメッセージ */
  error: string | null;

  /**
   * イベント取得（GET）
   * @param fieldId - 圃場 ID
   * @param asOf - ステート計算の基準日（省略時はサーバーの現在日付）
   */
  fetchFieldEvents: (fieldId: string, asOf?: Date) => Promise<void>;

  /**
   * イベント追加（POST）
   * fieldEventService.addEvent() を呼び出し、更新されたレスポンスで Store を更新
   */
  addEvent: (fieldId: string, input: NewFieldEventInput) => Promise<void>;

  /**
   * 防除実施記録のアンチェック (物理削除、 DELETE)。
   * fieldEventService.deleteEvent を呼び出し、 削除後の events + state で Store を更新する。
   * @param fieldId - 圃場 ID
   * @param eventId - 削除対象の FieldEvent ID
   */
  deleteEvent: (fieldId: string, eventId: string) => Promise<void>;

  /**
   * 複数圃場のイベントを一括取得
   * @param fieldIds - 圃場 ID の配列
   * @param asOf - ステート計算の基準日（省略時はサーバーの現在日付）
   */
  fetchMultipleFieldEvents: (fieldIds: string[], asOf?: Date) => Promise<void>;

  /**
   * 全圃場のイベントを指定日付で再取得（デバッグ用日付変更時）
   * @param asOf - ステート計算の基準日
   */
  refetchAllWithDate: (asOf?: Date) => Promise<void>;

  /**
   * 出芽予定日（EMERGENCE/SCHEDULED）の HARC 予測値を最新化する。
   *
   * 「播種後・出芽前」という時期限定のフローのため呼び出し側で条件判定すること
   * （通常は `state === 'BEFORE_EMERGENCE'` の圃場でのみ呼ぶ）。
   * 更新が行われた場合はストアの events 配列を upsert する。
   * 204 No Content（HARC 失敗・EMERGENCE/ACTUAL 既登録など）の場合は何もしない。
   *
   * @param fieldId - 圃場 ID
   * @returns API 呼び出しが（200 / 204 のいずれかで）正常完了した場合は true、
   *          ネットワーク失敗・4xx・5xx で失敗した場合は false。
   *          呼び出し元で「成功時のみ呼び出し済みフラグを立てる」用途を想定。
   */
  refreshEmergence: (fieldId: string) => Promise<boolean>;

  /**
   * 複数圃場の出芽予定日を並列で refresh する。個別失敗は他に影響させない。
   *
   * @param fieldIds - 対象圃場 ID の配列
   * @returns 正常完了した圃場 ID の配列（呼び出し元のリトライ判定に使用）
   */
  refreshEmergenceForFields: (fieldIds: string[]) => Promise<string[]>;

  /**
   * 特定圃場のイベント＆ステートを取得
   */
  getFieldEvents: (fieldId: string) => FieldEventsResponse | undefined;

  /**
   * キャッシュをクリア
   */
  clearCache: () => void;
}

/**
 * 圃場イベントを管理するZustandストア
 *
 * 圃場ごとのイベント取得・追加・ステート再計算を提供する。
 */
export const useFieldEventStore = create<FieldEventStore>((set, get) => ({
  fieldEventsMap: {},
  loading: false,
  error: null,

  fetchFieldEvents: async (fieldId: string, asOf?: Date) => {
    // asOf 未指定かつキャッシュにある場合はスキップ
    if (!asOf && get().fieldEventsMap[fieldId]) {
      return;
    }

    await withLoading(
      set,
      async () => {
        const response = await fieldEventService.getFieldEvents(fieldId, asOf);
        set((state) => ({
          fieldEventsMap: {
            ...state.fieldEventsMap,
            [fieldId]: response,
          },
        }));
      },
      'イベントの取得に失敗しました'
    );
  },

  addEvent: async (fieldId: string, input: NewFieldEventInput) => {
    await withLoading(
      set,
      async () => {
        const response = await fieldEventService.addEvent(fieldId, input);
        set((state) => ({
          fieldEventsMap: {
            ...state.fieldEventsMap,
            [fieldId]: response,
          },
        }));
      },
      'イベントの追加に失敗しました'
    );
  },

  deleteEvent: async (fieldId: string, eventId: string) => {
    await withLoading(
      set,
      async () => {
        const response = await fieldEventService.deleteEvent(fieldId, eventId);
        set((state) => ({
          fieldEventsMap: {
            ...state.fieldEventsMap,
            [fieldId]: response,
          },
        }));
      },
      'イベントの削除に失敗しました'
    );
  },

  fetchMultipleFieldEvents: async (fieldIds: string[], asOf?: Date) => {
    // asOf 指定時は全圃場を再取得、未指定時は未キャッシュのみ
    const targetFieldIds = asOf ? fieldIds : fieldIds.filter((id) => !get().fieldEventsMap[id]);
    if (targetFieldIds.length === 0) return;

    await withLoading(
      set,
      async () => {
        const results = await Promise.all(
          targetFieldIds.map((fieldId) => fieldEventService.getFieldEvents(fieldId, asOf))
        );
        set((state) => {
          const newMap = { ...state.fieldEventsMap };
          for (const response of results) {
            newMap[response.fieldId] = response;
          }
          return { fieldEventsMap: newMap };
        });
      },
      'イベントの取得に失敗しました'
    );
  },

  refetchAllWithDate: async (asOf?: Date) => {
    const { fieldEventsMap } = get();
    const fieldIds = Object.keys(fieldEventsMap);
    if (fieldIds.length === 0) return;

    fieldEventService.clearCache();

    await withLoading(
      set,
      async () => {
        const results = await Promise.all(
          fieldIds.map((fieldId) => fieldEventService.getFieldEvents(fieldId, asOf))
        );
        // 既存のマップにマージ（再取得中に追加された他の圃場を消さない）
        set((state) => {
          const merged = { ...state.fieldEventsMap };
          for (const response of results) {
            merged[response.fieldId] = response;
          }
          return { fieldEventsMap: merged };
        });
      },
      'イベントの取得に失敗しました'
    );
  },

  refreshEmergence: async (fieldId: string) => {
    let updated: FieldEvent | null;
    try {
      updated = await fieldEventService.refreshEmergence(fieldId);
    } catch (e) {
      // 4xx/ネットワーク失敗時はストアに副作用を出さず、コンソールに警告のみ。
      // ユーザー体験を妨げないよう loading/error には反映しない。
      // 呼び出し元が「失敗 → 同セッション中の再試行を許可」できるよう false を返す。
      console.warn('出芽予定日 refresh に失敗しました', { fieldId, error: e });
      return false;
    }
    if (!updated) return true;

    const event = updated;
    set((state) => {
      const existing = state.fieldEventsMap[fieldId];
      if (!existing) {
        // 当該圃場のイベントがまだ取得されていないケース。
        // この refresh だけでは state は確定できないので、後続の getFieldEvents 呼び出しで上書きされる前提で BEFORE_EMERGENCE を仮置き。
        return {
          fieldEventsMap: {
            ...state.fieldEventsMap,
            [fieldId]: { fieldId, state: 'BEFORE_EMERGENCE', events: [event] },
          },
        };
      }
      // 同 eventType/recordType/sequenceNumber を置換。なければ追加（upsert）
      const idx = existing.events.findIndex(
        (e) =>
          e.eventType === event.eventType &&
          e.recordType === event.recordType &&
          e.sequenceNumber === event.sequenceNumber
      );
      const events = [...existing.events];
      if (idx >= 0) {
        events[idx] = event;
      } else {
        events.push(event);
      }
      return {
        fieldEventsMap: {
          ...state.fieldEventsMap,
          [fieldId]: { ...existing, events },
        },
      };
    });
    return true;
  },

  refreshEmergenceForFields: async (fieldIds: string[]) => {
    if (fieldIds.length === 0) return [];
    const { refreshEmergence } = get();
    // 個別失敗は他に影響させない（refreshEmergence 内で例外を握っている）
    const results = await Promise.all(
      fieldIds.map(async (id) => ({ id, success: await refreshEmergence(id) }))
    );
    return results.filter((r) => r.success).map((r) => r.id);
  },

  getFieldEvents: (fieldId: string) => {
    return get().fieldEventsMap[fieldId];
  },

  clearCache: () => {
    fieldEventService.clearCache();
    set({ fieldEventsMap: {} });
  },
}));
